import { z } from 'zod';
import { autoCamelCaseMapping } from '@common/services/fieldMapping';

export const severitySchema = z.string().regex(/^-1|0|1$/);

const dbTaxBookingDiagnosticsObject = {
  COMPANY_CODE: z.string().nullish(),
  CC_DESCRIPTION: z.string().nullish(),
  LE: z.string().nullish(),
  TBE_COMPANY_CODE: z.string().nullish(),
  TBE_CC_DESCRIPTION: z.string().nullish(),
  TBE_LE: z.string().nullish(),
  CURRENT_PERIOD_CALC_STATUS: z.string().nullish(),
  PREVIOUS_PERIOD_CALC_STATUS: z.string().nullish(),
  REPORT: z.string(),
  SEVERITY: severitySchema,
  CONN_NAME: z.string().nullish(),
  TBE_CONN_NAME: z.string().nullish(),
};
export const dbTaxBookingDiagnosticsSchema = z.object(dbTaxBookingDiagnosticsObject).strict();
export const dbTaxBookingDiagnosticsListSchema = z.array(z.object(dbTaxBookingDiagnosticsObject).strict());
export const taxBookingDiagnosticsSchema = z.object(autoCamelCaseMapping(dbTaxBookingDiagnosticsObject)).strict();
export const taxBookingDiagnosticsListSchema = z.array(taxBookingDiagnosticsSchema);

export type DbTaxBookingDiagnostics = z.infer<typeof dbTaxBookingDiagnosticsSchema>;
export type DbTaxBookingDiagnosticsList = z.infer<typeof dbTaxBookingDiagnosticsListSchema>;
export type TaxBookingDiagnostics = z.infer<typeof taxBookingDiagnosticsSchema>;
export type TaxBookingDiagnosticsList = z.infer<typeof taxBookingDiagnosticsListSchema>;

const dbCompanyCodeDiagnosticsObject = {
  COMPANY_CODE: z.string(),
  CC_DESCRIPTION: z.string().nullish(),
  LEGAL_ENTITY: z.string().nullish(),
  BUSINESS_UNIT: z.string().nullish(),
  CONNECTION_NAME: z.string().nullish(),
  CURRENT_PERIOD_CALC_STATUS: z.string().nullish(),
  PREVIOUS_PERIOD_CALC_STATUS: z.string().nullish(),
  REPORT: z.string(),
  SEVERITY: severitySchema,
};
export const dbCompanyCodeDiagnosticsSchema = z.object(dbCompanyCodeDiagnosticsObject).strict();
export const dbCompanyCodeDiagnosticsListSchema = z.array(dbCompanyCodeDiagnosticsSchema);
export const companyCodeDiagnosticsSchema = z.object(autoCamelCaseMapping(dbCompanyCodeDiagnosticsObject)).strict();
export const companyCodeDiagnosticsListSchema = z.array(companyCodeDiagnosticsSchema);

export type DbCompanyCodeDiagnostics = z.infer<typeof dbCompanyCodeDiagnosticsSchema>;
export type DbCompanyCodeDiagnosticsList = z.infer<typeof dbCompanyCodeDiagnosticsListSchema>;
export type CompanyCodeDiagnostics = z.infer<typeof companyCodeDiagnosticsSchema>;
export type CompanyCodeDiagnosticsList = z.infer<typeof companyCodeDiagnosticsListSchema>;

const dbObligationDiagnosticsObject = {
  COMPANY_CODE: z.string(),
  CC_DESCRIPTION: z.string().nullish(),
  COUNTRY_TAX_TYPE: z.string().nullish(),
  CONNECTION_NAME: z.string().nullish(),
  CURRENT_PERIOD_CALC_STATUS: z.string().nullish(),
  PREVIOUS_PERIOD_CALC_STATUS: z.string().nullish(),
  REPORT: z.string(),
  SEVERITY: severitySchema,
};
export const dbObligationDiagnosticsSchema = z.object(dbObligationDiagnosticsObject).strict();
export const dbObligationDiagnosticsListSchema = z.array(dbObligationDiagnosticsSchema);
export const obligationDiagnosticsSchema = z.object(autoCamelCaseMapping(dbObligationDiagnosticsObject)).strict();
export const obligationDiagnosticsListSchema = z.array(obligationDiagnosticsSchema);

export type DbObligationDiagnostics = z.infer<typeof dbObligationDiagnosticsSchema>;
export type DbObligationDiagnosticsList = z.infer<typeof dbObligationDiagnosticsListSchema>;
export type ObligationDiagnostics = z.infer<typeof obligationDiagnosticsSchema>;
export type ObligationDiagnosticsList = z.infer<typeof obligationDiagnosticsListSchema>;

const dbCurrenciesDiagnosticsObject = {
  FROM_CURRENCY: z.string().nullish(),
  TO_CURRENCY: z.string().nullish(),
  FROM_CURR_PREC: z.number().nullish(),
  TO_CURR_PREC: z.number().nullish(),
  REPORT: z.string(),
  SEVERITY: severitySchema,
};
export const dbCurrenciesDiagnosticsSchema = z.object(dbCurrenciesDiagnosticsObject).strict();
export const dbCurrenciesDiagnosticsListSchema = z.array(dbCurrenciesDiagnosticsSchema);
export const currenciesDiagnosticsSchema = z.object(autoCamelCaseMapping(dbCurrenciesDiagnosticsObject)).strict();
export const currenciesDiagnosticsListSchema = z.array(currenciesDiagnosticsSchema);

export type DbCurrenciesDiagnostics = z.infer<typeof dbCurrenciesDiagnosticsSchema>;
export type DbCurrenciesDiagnosticsList = z.infer<typeof dbCurrenciesDiagnosticsListSchema>;
export type CurrenciesDiagnostics = z.infer<typeof currenciesDiagnosticsSchema>;
export type CurrenciesDiagnosticsList = z.infer<typeof currenciesDiagnosticsListSchema>;

const dbLegalEntityDiagnosticsObject = {
  LEGAL_ENTITY: z.string(),
  LE_DESCRIPTION: z.string().nullish(),
  US_TAX_TYPE: z.string().nullish(),
  ES_TAX_TYPE: z.string().nullish(),
  REPORT: z.string(),
  SEVERITY: severitySchema,
};
export const dbLegalEntityDiagnosticsSchema = z.object(dbLegalEntityDiagnosticsObject).strict();
export const dbLegalEntityDiagnosticsListSchema = z.array(dbLegalEntityDiagnosticsSchema);
export const legalEntityDiagnosticsSchema = z.object(autoCamelCaseMapping(dbLegalEntityDiagnosticsObject)).strict();
export const legalEntityDiagnosticsListSchema = z.array(legalEntityDiagnosticsSchema);

export type DbLegalEntityDiagnostics = z.infer<typeof dbLegalEntityDiagnosticsSchema>;
export type DbLegalEntityDiagnosticsList = z.infer<typeof dbLegalEntityDiagnosticsListSchema>;
export type LegalEntityDiagnostics = z.infer<typeof legalEntityDiagnosticsSchema>;
export type LegalEntityDiagnosticsList = z.infer<typeof legalEntityDiagnosticsListSchema>;

const dbProvisionDiagnosticsObject = {
  COMPANY_CODE: z.string(),
  CC_DESCRIPTION: z.string().nullish(),
  CURRENT_PERIOD_CALC_STATUS: z.string().nullish(),
  PREVIOUS_PERIOD_CALC_STATUS: z.string().nullish(),
  REPORT: z.string(),
  SEVERITY: severitySchema,
};
export const dbProvisionDiagnosticsSchema = z.object(dbProvisionDiagnosticsObject).strict();
export const dbProvisionDiagnosticsListSchema = z.array(dbProvisionDiagnosticsSchema);
export const provisionDiagnosticsSchema = z.object(autoCamelCaseMapping(dbProvisionDiagnosticsObject)).strict();
export const provisionDiagnosticsListSchema = z.array(provisionDiagnosticsSchema);

export type DbProvisionDiagnostics = z.infer<typeof dbProvisionDiagnosticsSchema>;
export type DbProvisionDiagnosticsList = z.infer<typeof dbProvisionDiagnosticsListSchema>;
export type ProvisionDiagnostics = z.infer<typeof provisionDiagnosticsSchema>;
export type ProvisionDiagnosticsList = z.infer<typeof provisionDiagnosticsListSchema>;
